import React from 'react';
import { useMonthlyReset } from '@/hooks/useMonthlyReset';
import { Calendar, Clock, RotateCcw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

interface MonthlyResetCountdownProps {
  variant?: 'full' | 'compact' | 'mini';
  className?: string;
}

export const MonthlyResetCountdown: React.FC<MonthlyResetCountdownProps> = ({
  variant = 'full',
  className = '',
}) => {
  const timeLeft = useMonthlyReset();
  const { t } = useLanguage();
  
  // Détecte si il reste 7 jours ou moins
  const isUrgent = timeLeft.days <= 7;

  // MINI — ajoute minutes & secondes en ultra court (d/h/m/s) sans changer la logique
  if (variant === 'mini') {
    return (
      <div className={`flex items-center gap-2 ${isUrgent ? 'text-base' : 'text-sm'} font-mono ${className}`}>
        <RotateCcw className={`w-4 h-4 ${isUrgent ? 'text-red-500' : 'text-gaming-accent'} animate-spin-slow`} />
        <span className="text-white">
          {t('reset.resetIn')}{' '}
          <span className={`${isUrgent ? 'text-red-500' : 'text-gaming-accent'} font-bold`}>
            {timeLeft.days}
            {t('reset.days').substring(0, 1)}{' '}
            {String(timeLeft.hours).padStart(2, '0')}
            {t('reset.hours').substring(0, 1)}{' '}
            {String(timeLeft.minutes).padStart(2, '0')}
            {t('reset.minutes').substring(0, 1)}{' '}
            {String(timeLeft.seconds).padStart(2, '0')}
            {t('reset.seconds').substring(0, 1)}
          </span>
        </span>
      </div>
    );
  }

  // COMPACT — ajoute minutes & secondes en suivant la même DA
  if (variant === 'compact') {
    return (
      <Card className={`p-2 sm:p-4 bg-gradient-to-r from-slate-800 to-slate-700 ${isUrgent ? 'border-red-500/50' : 'border-gaming-accent/30'} ${className}`}>
        <div className="flex items-center gap-1.5 sm:gap-3">
          <div className="flex items-center gap-1 sm:gap-2">
            <Calendar className={`w-4 h-4 sm:w-5 sm:h-5 ${isUrgent ? 'text-red-500' : 'text-gaming-accent'}`} />
            <span className="text-white font-semibold text-xs sm:text-base">{t('reset.monthlyReset')}</span>
          </div>

          <div className={`flex items-center gap-1.5 sm:gap-4 font-mono text-sm sm:${isUrgent ? 'text-xl' : 'text-lg'}`}>
            <div className="text-center">
              <div className={`${isUrgent ? 'text-red-500' : 'text-gaming-accent'} font-bold`}>{timeLeft.days}</div>
              <div className="text-[0.6rem] sm:text-xs text-slate-400">{t('reset.days').toUpperCase()}</div>
            </div>
            <div className="text-center">
              <div className={`${isUrgent ? 'text-red-500' : 'text-gaming-accent'} font-bold`}>{String(timeLeft.hours).padStart(2, '0')}</div>
              <div className="text-[0.6rem] sm:text-xs text-slate-400">{t('reset.hours').toUpperCase()}</div>
            </div>
            <div className="text-center">
              <div className={`${isUrgent ? 'text-red-500' : 'text-gaming-accent'} font-bold`}>{String(timeLeft.minutes).padStart(2, '0')}</div>
              <div className="text-[0.6rem] sm:text-xs text-slate-400">{t('reset.minutes').toUpperCase()}</div>
            </div>
            <div className="text-center">
              <div className={`${isUrgent ? 'text-red-500' : 'text-gaming-accent'} font-bold`}>{String(timeLeft.seconds).padStart(2, '0')}</div>
              <div className="text-[0.6rem] sm:text-xs text-slate-400">{t('reset.seconds').toUpperCase()}</div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // FULL — inchangé (déjà minutes & secondes)
  return (
    <Card className={`p-3 sm:p-6 bg-gradient-to-br from-slate-800 to-slate-900 border-gaming-accent/40 ${className}`}>
      <div className="text-center space-y-2 sm:space-y-4">
        <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-2">
          <RotateCcw className="w-4 h-4 sm:w-6 sm:h-6 text-gaming-accent animate-spin-slow" />
          <h3 className="text-base sm:text-xl font-bold text-white">{t('reset.monthlyResetTitle')}</h3>
          <RotateCcw className="w-4 h-4 sm:w-6 sm:h-6 text-gaming-accent animate-spin-slow" />
        </div>

        <p className="text-slate-300 text-xs sm:text-sm">
          {t('reset.description2')}
        </p>

        <div className="grid grid-cols-4 gap-2 sm:gap-4 mt-4 sm:mt-6">
          <div className="text-center">
            <div className={`${isUrgent ? 'bg-red-500/20 border-red-500/50' : 'bg-gaming-accent/20 border-gaming-accent/30'} rounded-lg p-2 sm:p-3 border`}>
              <div className={`${isUrgent ? 'text-xl sm:text-3xl text-red-500' : 'text-lg sm:text-2xl text-gaming-accent'} font-bold font-mono`}>
                {timeLeft.days}
              </div>
              <div className="text-[0.6rem] sm:text-xs text-slate-400 mt-0.5 sm:mt-1">{t('reset.days').toUpperCase()}</div>
            </div>
          </div>
          <div className="text-center">
            <div className={`${isUrgent ? 'bg-red-500/20 border-red-500/50' : 'bg-gaming-accent/20 border-gaming-accent/30'} rounded-lg p-2 sm:p-3 border`}>
              <div className={`${isUrgent ? 'text-xl sm:text-3xl text-red-500' : 'text-lg sm:text-2xl text-gaming-accent'} font-bold font-mono`}>
                {String(timeLeft.hours).padStart(2, '0')}
              </div>
              <div className="text-[0.6rem] sm:text-xs text-slate-400 mt-0.5 sm:mt-1">{t('reset.hours').toUpperCase()}</div>
            </div>
          </div>
          <div className="text-center">
            <div className={`${isUrgent ? 'bg-red-500/20 border-red-500/50' : 'bg-gaming-accent/20 border-gaming-accent/30'} rounded-lg p-2 sm:p-3 border`}>
              <div className={`${isUrgent ? 'text-xl sm:text-3xl text-red-500' : 'text-lg sm:text-2xl text-gaming-accent'} font-bold font-mono`}>
                {String(timeLeft.minutes).padStart(2, '0')}
              </div>
              <div className="text-[0.6rem] sm:text-xs text-slate-400 mt-0.5 sm:mt-1">{t('reset.minutes').toUpperCase()}</div>
            </div>
          </div>
          <div className="text-center">
            <div className={`${isUrgent ? 'bg-red-500/20 border-red-500/50' : 'bg-gaming-accent/20 border-gaming-accent/30'} rounded-lg p-2 sm:p-3 border`}>
              <div className={`${isUrgent ? 'text-xl sm:text-3xl text-red-500' : 'text-lg sm:text-2xl text-gaming-accent'} font-bold font-mono`}>
                {String(timeLeft.seconds).padStart(2, '0')}
              </div>
              <div className="text-[0.6rem] sm:text-xs text-slate-400 mt-0.5 sm:mt-1">{t('reset.seconds').toUpperCase()}</div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mt-2 sm:mt-4 text-[0.65rem] sm:text-xs text-slate-400">
          <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
          <span>{t('reset.newOpportunities')}</span>
        </div>
      </div>
    </Card>
  );
};
